import classNames from 'classnames';
import { motion } from 'motion/react';
import { Button, Disclosure, DisclosurePanel } from 'react-aria-components/Disclosure';
import { DisclosureGroup } from 'react-aria-components/DisclosureGroup';

import { MenuItemState } from '../../../../../app/layout/MenuItemState';
import { NavigationTranslation } from '../../../../../app/localization/SiteTranslation';
import Icon from '../../../../../app/shared/components/Icon/Icon';
import Link from '../../../../../app/shared/components/Link';
import css from './HamburgerMenuNav.css';

const NAV_ITEM_LIST_ANIMATION_VARIANTS = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.3 } },
};

const NAV_ITEM_ANIMATION_VARIANTS = {
  hidden: { opacity: 0, x: 20, y: -14 },
  visible: { opacity: 1, x: 0, y: 0, transition: { duration: 0.4, ease: 'easeOut' as const } },
};

interface HamburgerMenuNavProps {
  items: MenuItemState[];
  translation: NavigationTranslation;
  className?: string;
}

export default function HamburgerMenuNav({ className, items, translation }: HamburgerMenuNavProps) {
  if (items.length === 0) {
    return null;
  }

  const defaultExpandedKey = items.find((i) => i.isActive)?.key;

  return (
    <nav className={className} aria-label={translation.title}>
      <DisclosureGroup defaultExpandedKeys={defaultExpandedKey ? [defaultExpandedKey] : undefined}>
        <motion.ul initial="hidden" animate="visible" variants={NAV_ITEM_LIST_ANIMATION_VARIANTS}>
          {items.map((item) => (
            <motion.li variants={NAV_ITEM_ANIMATION_VARIANTS} key={item.key}>
              <HamburgerMenuNavItem item={item} />
            </motion.li>
          ))}
        </motion.ul>
      </DisclosureGroup>
    </nav>
  );
}

interface HamburgerMenuNavItemProps {
  item: MenuItemState;
}

function HamburgerMenuNavItem({ item }: HamburgerMenuNavItemProps) {
  if (item.isGroupingItem) {
    return <HamburgerMenuNavItemDisclosure item={item} />;
  }

  return (
    <Link
      className={css.link}
      href={item.data.url}
      target={item.data.target}
      aria-current={item.isActive ? 'page' : undefined}
    >
      {item.data.name}
    </Link>
  );
}

const SUBITEM_LIST_ANIMATION_VARIANTS = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.03, delayChildren: 0.05 } },
};

const SUBITEM_ANIMATION_VARIANTS = {
  hidden: { opacity: 0, x: 12 },
  visible: { opacity: 1, x: 0, y: 0, transition: { duration: 0.3, ease: 'easeOut' as const } },
};

interface HamburgerMenuNavItemDisclosureProps {
  item: MenuItemState;
}

function HamburgerMenuNavItemDisclosure({ item }: HamburgerMenuNavItemDisclosureProps) {
  return (
    <Disclosure id={item.key} className={classNames(css.disclosure, { [css.active]: item.isActive })}>
      {({ isExpanded }) => (
        <>
          <h2 className={css.heading}>
            <Button slot="trigger" className={css.button}>
              <span className={css.buttonLabel}>{item.data.name}</span>
              <Icon className={css.icon} name="angleDown" />
            </Button>
          </h2>
          <DisclosurePanel className={css.disclosurePanel}>
            <motion.ul
              className={css.disclosureItemList}
              initial="hidden"
              animate={isExpanded ? 'visible' : 'hidden'}
              variants={SUBITEM_LIST_ANIMATION_VARIANTS}
            >
              {item.children.map((child) => (
                <motion.li variants={SUBITEM_ANIMATION_VARIANTS} key={child.key}>
                  <Link
                    className={css.disclosureItemLink}
                    href={child.data.url}
                    target={child.data.target}
                    aria-current={child.isActive ? 'page' : undefined}
                  >
                    {child.data.name}
                  </Link>
                </motion.li>
              ))}
            </motion.ul>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
}
